import { v4 } from "uuid";
import { API_URL, call, debug, put, select } from "../../lib";
import { baseRequest, BaseRequestResult, retryAfterSeconds } from "../../lib/request";
import { clearIdentitySession } from "../../lib/auth/clear_identity_session";
import { SessionScope, isSessionScopeCurrent } from "../../lib/auth/session_scope";
import { QuestionnaireDraftKey, QuestionnaireDraftSnapshot } from "../../model/questionnaire_draft";
import { doSESSION_END_REASON_SET, SessionEndReason } from "../state";
import { getToken } from "./token_fetcher";
import { doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT } from "./identity_refresh_token_submit";

// a draft can be most of a megabyte, but a request that hangs would hold every later save of it back
export const QUESTIONNAIRE_DRAFT_REQUEST_TIMEOUT_MS = 30000;

export type QuestionnaireDraftRequestResult =
  | { kind: "ok"; payload: any }
  // a network failure, a timeout or a 5xx
  | { kind: "retry" }
  | { kind: "rate_limited"; retryAfterMs: number | null }
  // any other 4xx but 401
  | { kind: "refused" }
  // the session the request belonged to is over in this tab, or the request was abandoned
  | { kind: "stale" };

const leaveForSignIn = () => {
  window.location.href = "/";
};

// A 401 for a token this tab still holds as valid means the session is over, so the tab signs out at
// once: no refresh, no replay, and no last attempt to save drafts with a token that no longer works.
// Ending the scope first means every other draft request still out finds it stale, so this runs once.
function* signOutForEndedSession() {
  yield call(clearIdentitySession, { sessionEnded: true });
  // after the sign-out, which wipes the session record this persists into
  yield put(doSESSION_END_REASON_SET({ reason: SessionEndReason.SessionEnded, at: Date.now() }));
  yield call(leaveForSignIn);
}

// One draft request for the session `scope`. The scope is checked before the token wait, after it
// (immediately before the request goes out) and again before anything is reported, so a request
// from one session is never sent with another's token and its answer never reaches the next session.
// Nothing here is logged or put in the store: the body, and an axios or Joi error that quotes it,
// stay with the caller.
export function* runQuestionnaireDraftRequest({
  scope,
  path,
  data,
  signal,
}: {
  scope: SessionScope;
  path: string;
  data: Record<string, unknown>;
  signal?: AbortSignal;
}): Generator<any, QuestionnaireDraftRequestResult, any> {
  const abandoned = () => !isSessionScopeCurrent(scope) || Boolean(signal?.aborted);
  if (abandoned()) return { kind: "stale" };

  const accessToken: string | null = yield call(getToken);
  if (!accessToken || abandoned()) return { kind: "stale" };
  // read with the token, so a refresh that lands while the request is out cannot stand in for it
  const accessTokenExpiresAt: number =
    (yield select((state: any) => state?.auth?.accessTokenExpireTimestamp)) ?? 0;

  const result: BaseRequestResult = yield call(() =>
    baseRequest({
      method: "post",
      url: `${API_URL}${path}`,
      accessToken,
      data,
      signal,
      timeout: QUESTIONNAIRE_DRAFT_REQUEST_TIMEOUT_MS,
    }),
  );
  if (abandoned()) return { kind: "stale" };

  if (result.success) return { kind: "ok", payload: result.payload };
  debug("saga*runQuestionnaireDraftRequest failed", { path, statusCode: result.statusCode });
  if (result.statusCode === 401) {
    // The server answers 401 for an expired access token too, and while a refresh waits to try again
    // the tab goes on sending the token it has. A 401 for a token past its expiry says nothing about
    // the session, so the refresh decides, and the copy is sent again at the next tick.
    if (accessTokenExpiresAt <= Date.now()) {
      yield put(doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT({ requestId: v4() }));
      return { kind: "retry" };
    }
    yield call(signOutForEndedSession);
    return { kind: "stale" };
  }
  if (result.statusCode === 429) {
    const seconds = retryAfterSeconds(result.headers);
    return { kind: "rate_limited", retryAfterMs: seconds === null ? null : seconds * 1000 };
  }
  if (result.statusCode === null || result.statusCode >= 500) return { kind: "retry" };
  return { kind: "refused" };
}

// The body of a draft request: the key, with `taskId` left out for a new questionnaire
export const questionnaireDraftRequestData = (
  key: QuestionnaireDraftKey,
  draft?: QuestionnaireDraftSnapshot,
) => ({
  projectId: key.projectId,
  ...(key.taskId ? { taskId: key.taskId } : {}),
  ...(draft ? { draft } : {}),
});
